import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Utils from 'common/services/Utils';
import { ContractExpiredDashboardDto } from 'api/dashboard/dtos/ContractExpiredDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import dayjs from 'dayjs';
import { DATE_FORMAT_DEFAUT } from 'Config';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import KpiHeader from '../components/KpiHeader';
import WidgetPeriodSelect, { PeriodOption } from '../components/WidgetPeriodSelect';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import useInfiniteReveal from '../components/useInfiniteReveal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './ContractsExpired.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 15;

const PERIOD_OPTIONS: PeriodOption[] = [
	{ value: '30', labelKey: 'dashboard.contract_period_30' },
	{ value: '60', labelKey: 'dashboard.contract_period_60' },
	{ value: '90', labelKey: 'dashboard.contract_period_90' },
	{ value: 'custom', labelKey: 'dashboard.contract_period_custom' },
];

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const ContractsExpired = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<ContractExpiredDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [period, setPeriod] = useState<string>('90');
	const { dateFrom: customFrom, dateTo: customTo, setDateRange } = useWidgetFilters('contractsExpired');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	const getDateRange = () => {
		if (period === 'custom') {
			return { dateFrom: customFrom, dateTo: customTo };
		}
		const days = parseInt(period, 10);
		return {
			dateFrom: dayjs().subtract(days, 'day').startOf('day').toDate(),
			dateTo: dayjs().endOf('day').toDate(),
		};
	};

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const range = getDateRange();
				const criteria = {
					...(props.filters || {}),
					...vehicleScope,
					dateFrom: range.dateFrom,
					dateTo: range.dateTo,
				};
				const result = await DashboardService.getContractsExpired(criteria);
				if (!cancelled) {
					setData(result || []);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, period, customFrom, customTo]);

	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const navigateToContractDetail = (item: ContractExpiredDashboardDto) => {
		const type = item.contractType.toLowerCase();
		window.open(`/vehicles/details/${item.vehicleId}/contract/details/${type}/details/${item.contractId}`, '_blank');
	};

	const buildVehicleTooltip = (item: ContractExpiredDashboardDto) =>
		[item.brand, item.model].filter(Boolean).join(' ') || undefined;

	const totalMonthlyCost = data.reduce((sum, item) => sum + (item.monthlyCost || 0), 0);

	const renderOptions = () => (
		<WidgetPeriodSelect
			value={period}
			options={PERIOD_OPTIONS}
			onChange={setPeriod}
			dateFrom={customFrom}
			dateTo={customTo}
			onDateRangeChange={setDateRange}
		/>
	);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.contracts_expired') as string}
			renderOptions={renderOptions()}
			subTitleTooltip={t('dashboard.contracts_expired_tooltip' as any) as string}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={Utils.formatCurrency(totalMonthlyCost)}
						subLabel={`${data.length} · ${t('dashboard.contracts_expired_subtitle' as any)}`}
						currentTotal={totalMonthlyCost}
						previousTotal={null}
						previousRange={null}
					/>
					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.columns.vehicle')}</th>
									<th>{t('dashboard.contract_columns.type' as any)}</th>
									<th>{t('dashboard.contract_columns.number' as any)}</th>
									<th>{t('dashboard.contract_columns.expired_at' as any)}</th>
									<th>{t('dashboard.contract_columns.status' as any)}</th>
									<th className={styles.textRight}>{t('dashboard.contract_columns.monthly_cost' as any)}</th>
								</tr>
							</thead>
							<tbody>
								{visibleItems.map((item, index) => (
									<tr
										key={`${item.contractId}-${index}`}
										className={styles.tableRow}
										onClick={() => navigateToContractDetail(item)}
									>
										<td><span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="contracts-expired-vehicle-tooltip" data-place="right">{item.registrationNumber}</span></span></td>
										<td>{t(`dashboard.contract_type_${item.contractType.toLowerCase()}` as any)}</td>
										<td>{item.contractNumber || <span className={styles.muted}>—</span>}</td>
										<td>{dayjs(item.endDate).format(DATE_FORMAT_DEFAUT)}</td>
										<td>
											<span className={`${styles.badge} ${styles.badgeRed}`}>
												{t('dashboard.contract_badge_expired' as any)}
											</span>
										</td>
										<td className={styles.textRight}>{item.monthlyCost > 0 ? Utils.formatCurrency(item.monthlyCost) : <span className={styles.muted}>—</span>}</td>
									</tr>
								))}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={6} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="contracts-expired-vehicle-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default ContractsExpired;
